import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Star, TrendingUp, Users } from "lucide-react";
import { Link } from "react-router-dom";

function fmtGBP(v, symbol = '£') {
  const n = Number(v || 0);
  return `${symbol}${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function TeamPerformanceWidget({ currencySymbol = '£' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/dashboard/team-performance`);
        setData(res.data);
      } catch (e) { /* noop */ }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return null;
  if (!data) return null;

  const pct = data.salon_target > 0 ? Math.round((data.salon_actual / data.salon_target) * 100) : null;
  const ahead = data.salon_actual >= data.salon_target;

  return (
    <Card className="p-5 rounded-2xl border shadow-sm" data-testid="team-performance-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={16} className="text-primary" />
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>
            {data.mode === 'team' ? 'Team Performance' : 'Performance'} · {data.month}
          </h3>
        </div>
        <Link to="/admin/reports" className="text-xs text-primary hover:underline">View report →</Link>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="bg-muted/30 rounded-xl p-3" data-testid="tp-target">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-1">Target</p>
          <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{fmtGBP(data.salon_target, currencySymbol)}</p>
        </div>
        <div className="bg-muted/30 rounded-xl p-3" data-testid="tp-actual">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-1">Actual</p>
          <p className="text-lg font-semibold tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{fmtGBP(data.salon_actual, currencySymbol)}</p>
        </div>
        <div className="bg-muted/30 rounded-xl p-3" data-testid="tp-progress">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider mb-1">Progress</p>
          <p className={`text-lg font-semibold tabular-nums ${ahead ? 'text-green-600' : ''}`} style={{ fontFamily: 'Playfair Display' }}>
            {pct === null ? '—' : `${pct}%`}
          </p>
        </div>
      </div>

      {data.salon_target > 0 && (
        <div className="h-2 rounded-full bg-muted overflow-hidden mb-4">
          <div
            className={`h-full rounded-full ${ahead ? 'bg-green-500' : 'bg-primary'}`}
            style={{ width: `${Math.min(pct || 0, 100)}%` }}
          />
        </div>
      )}

      {data.mode === 'team' && data.staff && data.staff.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Team members</p>
          <div className="space-y-2">
            {data.staff.map(s => {
              const sPct = s.target > 0 ? Math.round((s.actual / s.target) * 100) : null;
              return (
                <div key={s.staff_id} className="flex items-center gap-3 p-2 bg-muted/20 rounded-xl" data-testid={`tp-staff-${s.staff_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium truncate">{s.staff_name}</p>
                      {s.avg_rating != null && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5">
                          <Star size={9} className="fill-amber-500 text-amber-500" />
                          {s.avg_rating.toFixed(1)} · {s.review_count}
                        </span>
                      )}
                    </div>
                    <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${s.actual >= s.target ? 'bg-green-500' : 'bg-primary'}`}
                        style={{ width: `${Math.min(sPct || 0, 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs font-semibold tabular-nums">{fmtGBP(s.actual, currencySymbol)}</p>
                    <p className="text-[10px] text-muted-foreground tabular-nums">of {fmtGBP(s.target, currencySymbol)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.mode === 'single' && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Users size={12} />
          <span>{data.salon_booking_count} bookings this month</span>
        </div>
      )}
    </Card>
  );
}
